import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useSlate } from 'slate-react';
import { Descendant, Editor as SlateEditor, NodeEntry, Path, Transforms, Location } from 'slate';
import { equals, any, startsWith } from 'ramda';
import { emptyLayout, isLayout } from 'slate/extensions/layout';
import { LayoutElement, PageElement } from 'slate/types/slate';
import Tippy from '@tippyjs/react';
import { ToolMenu } from 'app/components';
import { isParagraph } from 'slate/extensions/paragraph';
import { useEditorContext } from 'app/state/contexts/EditorContext';
import { sanitizeSlateHtml } from 'app/utils/htmlSerializer';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';

type StructureNode = { type?: string; className?: string; children?: Descendant[]; areas?: PageElement['areas'] };
type NodeSel = [StructureNode, Path];
type AreaConfig = { className?: string; hidden?: boolean };

const isPathOpen = (openPaths: Path[], path: Path) => {
  const pathOpen = openPaths.find((p) => {
    return p.join('') === path.join('');
  });

  return !!pathOpen;
};

const hasItemChildren = ({ children }: StructureNode) => {
  return !!children && !!children.length && children.some((n) => (n as StructureNode).type === 'layout');
};

const Toggle = ({ item, isOpen }: { item: StructureNode; isOpen: boolean }) => {
  const hasChildren = hasItemChildren(item);

  if (hasChildren) {
    return <i className={`wr-1 me-3 fa-regular fa-angle-${isOpen ? 'down' : 'right'}`}></i>;
  }

  return <i className="wr-1 me-3"></i>;
};

const CopyHtmlButton = () => {
  const { t } = useTranslation('template-editor');

  const { shadowHost } = useEditorContext();

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const pageHtml = shadowHost?.shadowRoot?.querySelector('.page-wrapper')?.innerHTML ?? '';
    const textarea = document.createElement('textarea');
    textarea.value = sanitizeSlateHtml(pageHtml);
    textarea.style.position = 'fixed';
    textarea.style.left = '-999999px';
    textarea.style.top = '-999999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const successful = document.execCommand('copy');
    if (successful) {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: t('layers.copied-html.success'),
        removeTime: 3000,
      });
    } else {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: t('layers.copied-html.error'),
        removeTime: 3000,
      });
    }
    document.body.removeChild(textarea);
  };

  return (
    <button className="btn text-primary" onClick={handleCopy}>
      <i className="fa-solid fa-copy"></i>
    </button>
  );
};

interface PageClassProps {
  template: { className?: string };
  index: number;
  onUpdateClassName: (className: string) => void;
}

const PageClass = ({ template, index, onUpdateClassName }: PageClassProps) => {
  const [editing, setEditing] = useState(false);
  const [className, setClassName] = useState(template.className);

  const editor = useSlate();

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing(true);
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setClassName(template.className);
    setEditing(false);
  };

  const handleEditClassName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClassName(e.target.value);
  };

  const handleUpdateClassName = () => {
    setEditing(false);
    Transforms.setNodes(
      editor,
      {
        class: className,
      } as Partial<PageElement>,
      {
        at: [index],
      }
    );
    onUpdateClassName(className ?? '');
  };

  const handleClickButtonWrapper = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (editing) {
    return (
      <>
        <div className="flex-1">
          <input type="text" className="form-control form-control-sm" value={className} onChange={handleEditClassName} />
        </div>
        <button className="btn ms-auto text-primary" onClick={handleCancelClick}>
          <i className="fa-regular fa-xmark" />
        </button>
        <button className="btn text-primary" onClick={handleUpdateClassName}>
          <i className="fa-regular fa-check" />
        </button>
      </>
    );
  }

  return (
    <>
      <div>
        <span className="badge bg-gray-200 border border-gray-300 m-0 me-1 mb-1">{template.className}</span>
      </div>
      <div className="ms-auto d-flex" onClick={handleClickButtonWrapper}>
        <CopyHtmlButton />
        <button className="btn text-primary" onClick={handleEditClick}>
          <i className="fa-regular fa-pen" />
        </button>
      </div>
    </>
  );
};

interface ChildClassesProps {
  item?: StructureNode;
  path?: Path;
  onClickEdit: (item: StructureNode, path: Path) => void;
  editNode?: NodeSel;
  onRemove: (entry: [StructureNode, Path]) => void;
}

const ChildClasses = ({ item = {}, path = [], onClickEdit, editNode = [{}, []], onRemove }: ChildClassesProps) => {
  const { t } = useTranslation('template');

  const arr = item.className?.split(' ') || [];
  const [, editPath] = editNode;
  const editing = equals(path, editPath);

  const [classes, setClasses] = useState(item.className);

  const editor = useSlate();

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClickEdit(item, path);
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClickEdit({}, []);
  };

  const handleEditClassNames = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClasses(e.target.value);
  };

  const handleUpdateClassNames = () => {
    Transforms.setNodes(
      editor,
      {
        className: classes,
      } as Partial<LayoutElement>,
      {
        at: path,
      }
    );

    onClickEdit({}, []);
  };

  const handleClickButtonWrapper = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleRemoveClick = () => {
    onRemove([item, path]);
  };

  const menuItems = [{ text: t('div.dropdown.remove'), icon: 'fa-regular fa-trash-can', onClick: handleRemoveClick }];

  if (editing) {
    return (
      <>
        <div className="flex-1">
          <input type="text" className="form-control form-control-sm" value={classes} onChange={handleEditClassNames} />
        </div>
        <button className="btn ms-auto text-primary" onClick={handleCancelClick}>
          <i className="fa-regular fa-xmark" />
        </button>
        <button className="btn text-primary" onClick={handleUpdateClassNames}>
          <i className="fa-regular fa-check" />
        </button>
      </>
    );
  }

  return (
    <>
      <div>
        {arr.map((cls: string, index: number) => {
          return (
            <span key={index} className="badge bg-gray-200 border border-gray-300 m-0 me-1 mb-1">
              {cls}
            </span>
          );
        })}
      </div>
      <div className="ms-auto d-flex" onClick={handleClickButtonWrapper}>
        <button className="btn text-primary" onClick={handleEditClick}>
          <i className="fa-regular fa-pen" />
        </button>
        <Tippy interactive={true} offset={[100, -60]} trigger="click" content={<ToolMenu menuItems={menuItems} />}>
          <button className="btn">
            <i className="fa-regular fa-ellipsis-v more toggle-icon edit-permission-group" />
          </button>
        </Tippy>
      </div>
    </>
  );
};

interface ChildProps {
  child: StructureNode;
  index: number;
  depth: number;
  onClick: (item: StructureNode, path: Path) => void;
  openPaths?: Path[];
  path?: Path;
  selectedNode?: NodeSel;
  onClickEdit: (item: StructureNode, path: Path) => void;
  editNode?: NodeSel;
  onRemove: (entry: [StructureNode, Path]) => void;
}

// TODO: We can replace depth with path length.
const Child = ({
  child,
  index: _index,
  depth,
  onClick,
  openPaths = [],
  path = [],
  selectedNode = [{}, []],
  onClickEdit,
  editNode = [{}, []],
  onRemove,
}: ChildProps) => {
  // It is open if the a open path starts with the childs path
  const isOpen = any(startsWith(path))(openPaths);
  const [, selectedPath] = selectedNode;

  const handleItemClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    onClick(child, path);
  };

  const isSelected = equals(selectedPath, path);
  const kids = (child.children ?? []) as StructureNode[];

  return (
    <div>
      <div className={`py-1 ps-3 clickable ${depth < 2 ? 'bg-blue-100 border-bottom border-gray-300' : 'ms-3'}`} onClick={handleItemClick}>
        <div className={`p-1 rounded me-1 d-flex align-items-center ${isSelected && 'bg-info text-light'} `}>
          <Toggle item={child} isOpen={isOpen} />
          <span className="me-5">{child.type === 'layout' ? 'div' : child.type}</span>
          <ChildClasses item={child} path={path} onClickEdit={onClickEdit} editNode={editNode} onRemove={onRemove} />
        </div>
      </div>
      {isOpen && kids.some((n) => n.type === 'layout')
        ? kids.map((childNode, i) => {
            if (childNode.type !== 'layout') {
              return null;
            }
            return (
              <Child
                child={childNode}
                index={i}
                depth={depth + 1}
                onClick={onClick}
                openPaths={openPaths}
                key={i}
                path={[...path, i]}
                selectedNode={selectedNode}
                onClickEdit={onClickEdit}
                editNode={editNode}
                onRemove={onRemove}
              />
            );
          })
        : null}
    </div>
  );
};

interface AreaItemProps {
  areaKey: 'header' | 'content' | 'footer';
  label: string;
  page: PageElement;
  pageIndex: number;
}

const AreaItem = ({ areaKey, label, page, pageIndex }: AreaItemProps) => {
  const area = (page.areas as Record<string, AreaConfig> | undefined)?.[areaKey];
  const [editing, setEditing] = useState(false);
  const [className, setClassName] = useState(area?.className || '');
  const editor = useSlate();
  const isHidden = area?.hidden;

  useEffect(() => {
    setClassName((page.areas as Record<string, AreaConfig> | undefined)?.[areaKey]?.className || '');
  }, [page.areas, areaKey]);

  const handleSave = () => {
    setEditing(false);
    const areas = { ...(page.areas || {}), [areaKey]: { ...area, className: className.trim() } };
    Transforms.setNodes(editor, { areas } as Partial<PageElement>, { at: [pageIndex] });
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setClassName(area?.className || '');
    setEditing(false);
  };

  const canToggleVisibility = areaKey === 'header' || areaKey === 'footer';

  const handleToggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentArea: AreaConfig = area || {};
    const newHidden = !currentArea.hidden;
    const updatedArea: AreaConfig = { ...currentArea };
    if (newHidden) {
      updatedArea.hidden = true;
    } else {
      delete updatedArea.hidden;
    }
    const areas = { ...(page.areas || {}), [areaKey]: updatedArea };
    Transforms.setNodes(editor, { areas } as Partial<PageElement>, { at: [pageIndex] });
  };

  const currentClasses = (area?.className || '').split(' ').filter(Boolean);

  return (
    <div className="py-1 ps-3 ms-3 border-bottom border-gray-200">
      <div className="p-1 rounded me-1 d-flex align-items-center">
        <i className="wr-1 me-3"></i>
        <span className={`me-3${isHidden ? ' text-muted' : ''}`}>{label}</span>
        {editing ? (
          <>
            <div className="flex-1">
              <input type="text" className="form-control form-control-sm" value={className} onChange={(e) => setClassName(e.target.value)} />
            </div>
            <button className="btn ms-auto text-primary" onClick={handleCancel}>
              <i className="fa-regular fa-xmark" />
            </button>
            <button className="btn text-primary" onClick={handleSave}>
              <i className="fa-regular fa-check" />
            </button>
          </>
        ) : (
          <>
            <div>
              {currentClasses.map((cls: string, i: number) => (
                <span key={i} className="badge bg-gray-200 border border-gray-300 m-0 me-1 mb-1">
                  {cls}
                </span>
              ))}
            </div>
            <div className="ms-auto d-flex" onClick={(e) => e.stopPropagation()}>
              {canToggleVisibility && (
                <button className="btn text-primary" onClick={handleToggleVisibility}>
                  <i className={`fa-regular ${isHidden ? 'fa-eye-slash text-muted' : 'fa-eye'}`} />
                </button>
              )}
              <button
                className="btn text-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(true);
                }}
              >
                <i className="fa-regular fa-pen" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface PageItemProps {
  page?: PageElement;
  index?: number;
  template: { className?: string };
  onTemplateClassNameChange: (className: string) => void;
  onClickItem: (item: StructureNode, path: Path) => void;
  openPaths?: Path[];
  selectedNode?: NodeSel;
  onEditNode: (item: StructureNode, path: Path) => void;
  editNode: NodeSel;
  onRemove: (entry: [StructureNode, Path]) => void;
}

const PageItem = ({
  page = { type: 'page', uuid: '', children: [] },
  index = 0,
  template,
  onTemplateClassNameChange,
  onClickItem,
  openPaths = [],
  selectedNode = [{}, []],
  onEditNode,
  editNode,
  onRemove,
}: PageItemProps) => {
  const children = (page.children ?? []) as StructureNode[];
  return (
    <div key={index}>
      <div className="ps-3 py-2 bg-blue-100 border-bottom border-gray-300">
        <div className={`p-1 rounded me-1 d-flex align-items-center`}>
          <Toggle item={page} isOpen />
          <span className="me-5">page</span>
          <PageClass template={template} index={index} onUpdateClassName={onTemplateClassNameChange} />
        </div>
      </div>
      <AreaItem areaKey="header" label="area-header" page={page} pageIndex={index} />
      <AreaItem areaKey="content" label="area-content" page={page} pageIndex={index} />
      {Array.isArray(page.children)
        ? children.map((child, i) => {
            return (
              <Child
                child={child}
                index={i}
                depth={2}
                onClick={onClickItem}
                openPaths={openPaths}
                key={i}
                path={[index, i]}
                selectedNode={selectedNode}
                onClickEdit={onEditNode}
                editNode={editNode}
                onRemove={onRemove}
              />
            );
          })
        : null}
      <AreaItem areaKey="footer" label="area-footer" page={page} pageIndex={index} />
    </div>
  );
};

interface TemplateStructureProps {
  template: { className?: string };
  onTemplateClassNameChange: (className: string) => void;
}

const TemplateStructure = ({ template, onTemplateClassNameChange }: TemplateStructureProps) => {
  const { t } = useTranslation(['template', 'template-editor']);

  const editor = useSlate();

  const [editNode, setEditNode] = useState<NodeSel>([{}, []]);

  const [openPaths, setOpenPaths] = useState<Path[]>([]);
  const [selectedNode, setSelectedNode] = useState<NodeSel>([{}, []]);

  useEffect(() => {
    if (!editor.lastSelection) {
      return;
    }

    const generator = SlateEditor.nodes(editor, {
      at: editor.lastSelection,
      match: isLayout,
      mode: 'lowest',
    });

    const [deepestLayoutNode, deepestLayoutPath] = generator.next().value as NodeEntry;

    if (deepestLayoutPath.length > 0) {
      setOpenPaths([...openPaths, deepestLayoutPath]);
      setSelectedNode([deepestLayoutNode as StructureNode, deepestLayoutPath]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.lastSelection]);

  const handleClickListItem = (item: StructureNode, path: Path) => {
    setSelectedNode([item, path]);

    if (!hasItemChildren(item)) {
      return;
    }

    const isOpen = isPathOpen(openPaths, path);

    if (isOpen) {
      setOpenPaths(openPaths.filter((openPath) => !startsWith(path, openPath)));
    } else {
      setOpenPaths([...openPaths, path]);
    }
  };

  const handleSetEditNode = (item: StructureNode, path: Path) => {
    setEditNode([item, path]);
  };

  const handleAddBefore = () => {
    const [, path] = selectedNode;
    Transforms.insertNodes(editor, emptyLayout(), {
      at: path,
    });
  };

  const handleAddInside = () => {
    const [node, path] = selectedNode;
    const child = node.children?.find(isParagraph);
    if (child) {
      Transforms.wrapNodes(editor, emptyLayout({ withParagraph: false, horizontal: !node.className?.includes('horizontal') }), {
        at: [...path, 0],
        split: false,
        match: isParagraph,
      });
    } else {
      Transforms.insertNodes(editor, emptyLayout({ horizontal: !node.className?.includes('horizontal') }), {
        at: [...path, 0],
      });
    }
  };

  const handleAddAfter = () => {
    const [, path] = selectedNode;
    const afterPath = [...path];
    afterPath[afterPath.length - 1]++;
    Transforms.insertNodes(editor, emptyLayout(), {
      at: afterPath,
    });
  };

  const handleRemove = ([, path]: [StructureNode, Path]) => {
    Transforms.removeNodes(editor, {
      at: path,
      match: isLayout,
      mode: 'lowest',
    });
  };

  const hasSelectedNode = Location.isLocation(selectedNode[1]);

  return (
    <div className="bg-light h-100 border-end border-gray-300 flex-1 me-3 position-relative">
      <h4 className="subheading px-3 pt-4">{t('template-editor:layers')}</h4>

      {(editor.children as PageElement[]).map((pageItem, index) => (
        <PageItem
          page={pageItem}
          key={index}
          index={index}
          onClickItem={handleClickListItem}
          openPaths={openPaths}
          selectedNode={selectedNode}
          onEditNode={handleSetEditNode}
          editNode={editNode}
          onRemove={handleRemove}
          template={template}
          onTemplateClassNameChange={onTemplateClassNameChange}
        />
      ))}
      {hasSelectedNode ? (
        <div id="template-editor-bottom-menu" className="position-fixed bottom-0 p-1 border-start border-gray-300">
          <span className="me-2">{t('template:add-div.title')}</span>
          <button className="btn btn-secondary me-1" onClick={handleAddBefore}>
            {t('template:add-div.before')}
          </button>
          <button className="btn btn-secondary me-1" onClick={handleAddInside}>
            {t('template:add-div.inside')}
          </button>
          <button className="btn btn-secondary me-1" onClick={handleAddAfter}>
            {t('template:add-div.after')}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default TemplateStructure;
